import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import { useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetModelQuery } from "store/queries/chat";
import styled from "styled-components";

import { PaginatedChatsTable } from "./components/PaginatedChatsTable";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const HeaderCard = styled(Card)`
  margin-bottom: 24px;
`;

const SectionCard = styled(Card)`
  margin-bottom: 20px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: #6c757d;
`;

const Value = styled.span`
  color: #333;
`;

const LinkText = styled.span`
  color: #0066cc;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const ChatItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const ChatMeta = styled.div`
  font-size: 0.9em;
  color: #6c757d;
  margin-top: 4px;
`;

function ModelViewPage() {
  const { modelId } = useParams<{ modelId: string }>();
  const navigate = useNavigate();
  const { data: model, isLoading, error } = useGetModelQuery(Number(modelId));

  const fileNameBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText onClick={() => navigate(`/chatbot/files/${rowData.fileId}`)}>
          {rowData.name}
        </LinkText>
      );
    },
    [navigate]
  );

  const fileTypeBody = useCallback((rowData: any) => {
    return (
      <div>
        <Tag
          value={rowData.identifier}
          severity={rowData.identifier === "Custom" ? "info" : "success"}
        />
        {rowData.isAutoIncluded && (
          <Tag
            value="Auto-Included"
            severity="warning"
            style={{ marginLeft: "8px" }}
          />
        )}
      </div>
    );
  }, []);

  const accountNameBody = useCallback(
    (rowData: any) => {
      return (
        <LinkText onClick={() => navigate(`/hospitals/view/${rowData.id}`)}>
          {rowData.name}
        </LinkText>
      );
    },
    [navigate]
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error || !model) {
    return (
      <Container>
        <Card>
          <h2>Error</h2>
          <p>Model not found or failed to load.</p>
          <Button
            label="Back to Models"
            icon="pi pi-arrow-left"
            onClick={() => navigate("/chatbot/models")}
          />
        </Card>
      </Container>
    );
  }

  // Combine auto-included files and custom files
  const allFiles = [];

  // Add auto-included files based on model type
  if (model.autoIncludedFiles) {
    if (model.type === "PatientChatBot") {
      if (model.autoIncludedFiles.eligibilityGuide) {
        allFiles.push({
          ...model.autoIncludedFiles.eligibilityGuide,
          isAutoIncluded: true,
        });
      }
      if (model.autoIncludedFiles.pricingGuide) {
        allFiles.push({
          ...model.autoIncludedFiles.pricingGuide,
          isAutoIncluded: true,
        });
      }
    } else if (model.type === "ContractManagementBot") {
      if (model.autoIncludedFiles.contractManagementGuide) {
        allFiles.push({
          ...model.autoIncludedFiles.contractManagementGuide,
          isAutoIncluded: true,
        });
      }
      // CLE-3043 — the Hospital Staff Bot's how-to guide.
      if (model.autoIncludedFiles.hospitalStaffGuide) {
        allFiles.push({
          ...model.autoIncludedFiles.hospitalStaffGuide,
          isAutoIncluded: true,
        });
      }
    }
  }

  // Add custom files
  if (model.customFiles) {
    allFiles.push(
      ...model.customFiles.map((file) => ({ ...file, isAutoIncluded: false }))
    );
  }

  const files = allFiles;

  return (
    <Container>
      <HeaderCard>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1>{model.name}</h1>
            <p className="text-muted">Chat Model Details</p>
          </div>
          <div>
            <Button
              label="Edit Model"
              icon="pi pi-pencil"
              className="p-button-outlined mr-2"
              onClick={() => navigate(`/chatbot/models/edit/${model.id}`)}
            />
            <Button
              label="Back to Models"
              icon="pi pi-arrow-left"
              className="p-button-secondary"
              onClick={() => navigate("/chatbot/models")}
            />
          </div>
        </div>
      </HeaderCard>

      <SectionCard title="Model Information">
        <DetailRow>
          <Label>Model ID:</Label>
          <Value>{model.id}</Value>
        </DetailRow>
        <DetailRow>
          <Label>Type:</Label>
          <Value>{model.type}</Value>
        </DetailRow>
        <DetailRow>
          <Label>Name:</Label>
          <Value>{model.name}</Value>
        </DetailRow>
        <DetailRow>
          <Label>Created:</Label>
          <Value>{new Date(model.createdAt).toLocaleDateString()}</Value>
        </DetailRow>
        <DetailRow>
          <Label>Last Updated:</Label>
          <Value>{new Date(model.updatedAt).toLocaleDateString()}</Value>
        </DetailRow>
      </SectionCard>

      <SectionCard title="Associated Files">
        {files.length > 0 ? (
          <DataTable
            value={files}
            responsiveLayout="scroll"
            emptyMessage="No files associated with this model."
          >
            <Column field="name" header="File Name" body={fileNameBody} />
            <Column field="identifier" header="Type" body={fileTypeBody} />
            <Column field="purpose" header="Purpose" />
            <Column
              field="createdAt"
              header="Created"
              body={(rowData) =>
                new Date(rowData.createdAt).toLocaleDateString()
              }
            />
          </DataTable>
        ) : (
          <p className="text-muted">No files associated with this model.</p>
        )}
      </SectionCard>

      <SectionCard title="Associated Accounts">
        {model.accounts && model.accounts.length > 0 ? (
          <DataTable
            value={model.accounts}
            responsiveLayout="scroll"
            emptyMessage="No accounts associated with this model."
          >
            <Column field="name" header="Account Name" body={accountNameBody} />
            <Column field="id" header="Account ID" />
          </DataTable>
        ) : (
          <p className="text-muted">No accounts associated with this model.</p>
        )}
      </SectionCard>

      <SectionCard title="Chat Sessions">
        <PaginatedChatsTable modelId={model.id} defaultPageSize={10} />
      </SectionCard>
    </Container>
  );
}

export default ModelViewPage;
